import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarCheck2, DollarSign, GitBranch, LineChart, FileDown, Users,
  ShieldAlert, Sparkles, Globe2, Mail, Boxes, Smartphone, Check, Minus,
  LibraryBig, Share2, FileText, Target, Activity, LayoutDashboard, Linkedin, Star, Instagram,
} from "lucide-react";

const CAPABILITIES = [
  { icon: CalendarCheck2, t: "Smart Gantt timeline", d: "Dependencies, lag, and critical path recalc instantly whenever anything moves." },
  { icon: LineChart, t: "Baseline vs actuals", d: "Snapshot a plan, log actuals, see schedule and budget variance in real time." },
  { icon: ShieldAlert, t: "Risk Register per project", d: "Track risks, link them to tasks, feed the project risk score automatically." },
  { icon: Sparkles, t: "AI copilots built-in", d: "Executive summaries with tone, AI-suggested risks, and a mitigation coach that rewrites weak plans." },
  { icon: LibraryBig, t: "Risk Library + CSV import", d: "Save common risks, auto-match by role or program, bulk-import from a spreadsheet." },
  { icon: Target, t: "Resource load & overbooking", d: "See who's over 100% capacity before the burnout emails arrive." },
  { icon: FileText, t: "Branded PDF status pack", d: "One-click status pack with Gantt, variance, risk register — mailed every Monday if you want." },
  { icon: Share2, t: "White-labelled client portal", d: "Public share link with your logo, colors, and opt-in Top-3 risks for stakeholder transparency." },
  { icon: Boxes, t: "Portfolios & programs", d: "Roll multiple projects into programs and portfolios with live totals." },
  { icon: Mail, t: "Weekly digest + slip alerts", d: "Automated Monday-morning email digest and smart slippage alerts the moment the plan drifts." },
  { icon: Globe2, t: "Multi-currency", d: "Set your org currency once — every variance, rollup, and PDF respects it." },
  { icon: Smartphone, t: "iOS + Android apps", d: "Native shell for both stores so your plan is in every pocket on the team." },
];

const SCREENSHOTS = [
  { src: "/screenshots/02-plan.png", t: "The whole plan on one screen", d: "Delivery date, cost, risk score, variances, timeline and tasks — one view, zero toggling." },
  { src: "/screenshots/03-risks.png", t: "Risks that feed the score", d: "Open risks, mitigations, owners and linked tasks — all nudging the 0-100 risk score live." },
  { src: "/screenshots/01-dashboard.png", t: "Tasks + Gantt, side by side", d: "See the plan and the timeline in one scroll — dependencies, estimates and progress bars line up with the task list." },
  { src: "/screenshots/04-portfolio.png", t: "Portfolios roll up live", d: "Group programs into portfolios and watch totals, variances and health recalc as you plan." },
];

const TIERS = [
  { id: "personal", name: "Personal", tagline: "Freelancers & curious evaluators", priceAnnual: "$0", priceMonthly: "$0", suffix: "/ month", cta: "Start free", href: "/register", highlight: false },
  { id: "personal-pro", name: "Personal Pro", tagline: "Solo PMs & consultants", priceAnnual: "$8", priceMonthly: "$10", suffix: "/ month", cta: "Start 14-day trial", href: "/register", highlight: true, ribbon: "Most popular" },
  { id: "enterprise", name: "Enterprise", tagline: "Teams & PMO cores", priceAnnual: "$20", priceMonthly: "$24", suffix: "/ seat / mo", noteAnnual: "Billed annually · 5 seat min", noteMonthly: "Billed monthly · 5 seat min", cta: "Contact sales", href: "mailto:sales@upliftc.com?subject=Compass%20PM%20Enterprise", highlight: false },
  { id: "enterprise-pro", name: "Enterprise Pro", tagline: "Fortune 1000 & VPMOs", priceAnnual: "Custom", priceMonthly: "Custom", suffix: "", note: "From ~$20 / seat / mo", cta: "Talk to us", href: "mailto:sales@upliftc.com?subject=Compass%20PM%20Enterprise%20Pro", highlight: false },
];

const COMPARISON = [
  { group: "Planning essentials", rows: [
    { label: "Active projects", vals: ["Up to 3", "Unlimited", "Unlimited", "Unlimited"] },
    { label: "Gantt + dependencies", vals: [true, true, true, true] },
    { label: "Critical path & baseline", vals: [false, true, true, true] },
    { label: "Reusable project templates", vals: [false, true, true, true] },
  ]},
  { group: "AI & risk", rows: [
    { label: "AI prompts / month", vals: ["10", "Unlimited", "Unlimited", "Priority"] },
    { label: "Risk Register & library", vals: ["Starter pack", true, true, true] },
    { label: "AI exec summary + mitigation coach", vals: [false, true, true, true] },
    { label: "CSV bulk import", vals: [false, true, true, true] },
  ]},
  { group: "Reporting & brand", rows: [
    { label: "Branded PDF + Monday digest", vals: [false, true, true, true] },
    { label: "White-label client portal", vals: [false, true, true, true] },
    { label: "Portfolio & program rollups", vals: [false, false, true, true] },
    { label: "Multi-currency", vals: [false, true, true, true] },
  ]},
  { group: "Team & platform", rows: [
    { label: "Teammate seats", vals: ["—", "1", "Unlimited", "Unlimited"] },
    { label: "Audit log", vals: [false, true, true, true] },
    { label: "SSO / SAML", vals: [false, false, "Add-on", true] },
    { label: "Dedicated CSM + SLA", vals: [false, false, false, true] },
  ]},
];

const cellRender = (v) => {
  if (v === true) return <Check size={16} className="text-emerald-400 mx-auto"/>;
  if (v === false) return <Minus size={14} className="text-slate-600 mx-auto"/>;
  return <span className="text-xs text-slate-200">{v}</span>;
};

export default function Splash() {
  const [annual, setAnnual] = useState(true);
  return (
    <div className="min-h-screen pc-grid-bg">
      <header className="pc-glass-header">
        <div className="max-w-[1300px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/compass-logo.png" alt="Compass PM logo" className="w-10 h-10 object-contain" data-testid="splash-brand-logo" />
            <div>
              <div className="font-bold text-white text-lg" style={{ fontFamily: "Outfit" }}>Compass PM</div>
              <div className="text-[11px] uppercase tracking-widest text-blue-300/70">by Uplift Communications</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
            <a href="#capabilities" className="hover:text-white">Capabilities</a>
            <a href="#screens" className="hover:text-white">In action</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <Link to="/about" className="hover:text-white" data-testid="splash-nav-about">About</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="splash-signin-btn" className="text-sm text-slate-300 hover:text-white px-3 py-2">Sign in</Link>
            <Link to="/register" data-testid="splash-trial-btn" className="text-sm bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-lg shadow-blue-900/30 hover:from-blue-400 hover:to-blue-600">
              Start free
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-[1300px] mx-auto px-6 pt-20 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/5 text-xs uppercase tracking-widest text-blue-300 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></span> The project plan that calculates itself
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6" style={{ fontFamily: "Outfit" }}>
            Fewer tools. Cleaner plans. <span className="bg-gradient-to-r from-blue-400 to-sky-300 bg-clip-text text-transparent">Truthful status.</span>
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed max-w-xl mb-8">
            Compass PM is a modern planning workspace for structured PMOs. Describe the work, assign roles, drop a few dependencies — delivery dates, budgets, variances, risk score and client-ready reports recalc the moment anything moves. No spreadsheets. No two-day retrofits. Just a plan the whole team can read in under a minute.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-create-account-btn" className="group inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-xl shadow-blue-900/40 hover:from-blue-400 hover:to-blue-600">
              Create free account <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform"/>
            </Link>
            <a href="#pricing" className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 text-slate-200 px-6 py-3 rounded-xl font-semibold hover:border-blue-400/40 hover:text-white">
              Compare plans
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-4">14-day free trial on Personal Pro · No credit card required · Cancel anytime</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
            {[
              { k: "Plan accuracy", v: "+28%" },
              { k: "Status prep", v: "–6h/wk" },
              { k: "Projects / PMO", v: "Unlimited" },
              { k: "Set-up time", v: "<15 min" },
            ].map((s) => (
              <div key={s.k} className="rounded-lg bg-slate-900/60 border border-slate-800 p-3">
                <div className="text-[10px] uppercase tracking-widest text-blue-300/80">{s.k}</div>
                <div className="text-lg font-bold text-white mt-0.5" style={{ fontFamily: "Outfit" }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute -inset-6 bg-blue-500/10 blur-3xl rounded-full"></div>
            <div className="pc-card p-2 relative overflow-hidden" data-testid="hero-video-card">
              <video
                className="w-full rounded-lg block"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/hero-poster.jpg"
                data-testid="hero-product-video"
              >
                <source src="/videos/hero-loop.mp4" type="video/mp4" />
                <source src="/videos/hero-loop-opt.webm" type="video/webm" />
              </video>
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/90 bg-slate-950/60 backdrop-blur-sm border border-white/10 px-2 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> Live product
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE STATEMENT */}
      <section className="max-w-[1300px] mx-auto px-6 pb-4" data-testid="value-statement">
        <div className="pc-card p-6 sm:p-10">
          <p className="text-lg sm:text-xl text-white font-semibold mb-6" style={{ fontFamily: "Outfit" }}>
            Built around clarity, simplicity, and accountability, CompassPM helps teams:
          </p>
          <ul className="space-y-3 mb-8" data-testid="value-list">
            {[
              ["Plan with confidence", "establish delivery expectations and budget estimates."],
              ["See the whole picture", "understand schedules, milestones, dependencies, and critical paths."],
              ["Measure performance", "compare progress against plan and identify potential delivery issues."],
              ["Communicate effortlessly", "simplify project status updates and stakeholder reporting."],
              ["Focus on execution", "reduce administrative overhead and keep attention on decisions, risks, and results."],
            ].map(([h, d]) => (
              <li key={h} className="flex gap-3 text-slate-300 leading-relaxed" data-testid={`value-item-${h.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                <span><span className="text-white font-semibold">{h}</span> — {d}</span>
              </li>
            ))}
          </ul>
          <p className="text-slate-300 leading-relaxed mb-4">
            Whether managing a single initiative or coordinating complex project delivery, CompassPM puts the information that matters within reach.
          </p>
          <p className="text-blue-300 font-semibold" style={{ fontFamily: "Outfit" }} data-testid="value-tagline">
            Less time managing the plan. More time delivering the outcome.
          </p>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="max-w-[1300px] mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/5 text-xs uppercase tracking-widest text-blue-300 mb-5">What you get</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "Outfit" }}>A full PMO stack, without the stack.</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mt-3">Everything a planner reaches for in a week, in a single workspace — timeline, risk, resources, reports, AI copilots.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAPABILITIES.map((c) => (
            <div key={c.t} className="pc-card p-5 hover:border-blue-500/40 transition-colors">
              <c.icon size={22} className="text-blue-400 mb-3"/>
              <div className="text-base font-semibold text-white mb-1.5" style={{ fontFamily: "Outfit" }}>{c.t}</div>
              <p className="text-slate-400 text-sm leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SCREENS */}
      <section id="screens" className="max-w-[1300px] mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/5 text-xs uppercase tracking-widest text-blue-300 mb-5">In action</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "Outfit" }}>See it the way your team will</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mt-3">Zero mock screenshots — every image below is live product.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {SCREENSHOTS.map((s, i) => (
            <div key={s.src} className="pc-card overflow-hidden" data-testid={`splash-screen-${i}`}>
              <div className={`${s.src.endsWith('01-dashboard.png') ? 'aspect-[1500/868]' : 'aspect-[1500/820]'} bg-slate-950 border-b border-slate-800 overflow-hidden`}>
                <img src={s.src} alt={s.t} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="text-base font-semibold text-white mb-1" style={{ fontFamily: "Outfit" }}>{s.t}</div>
                <p className="text-slate-400 text-sm">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-[1300px] mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/5 text-xs uppercase tracking-widest text-blue-300 mb-5">Pricing</div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{ fontFamily: "Outfit" }}>Simple pricing. Scales with the PMO.</h2>
          <p className="text-slate-400 max-w-2xl mx-auto mt-3">Start free, upgrade when the plan outgrows the spreadsheet. All prices USD.</p>
          <div className="mt-6 inline-flex rounded-lg border border-slate-700 overflow-hidden" data-testid="splash-billing-toggle">
            <button onClick={() => setAnnual(false)} data-testid="splash-toggle-monthly" className={`px-4 py-2 text-sm ${!annual ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} data-testid="splash-toggle-annual" className={`px-4 py-2 text-sm ${annual ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800"}`}>Annual · <span className="text-emerald-300">save 20%</span></button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {TIERS.map((t) => (
            <div key={t.id} data-testid={`splash-tier-${t.id}`}
              className={`pc-card p-6 relative ${t.highlight ? "border-blue-500/60 shadow-xl shadow-blue-900/30 ring-1 ring-blue-500/30" : ""}`}>
              {t.ribbon && (
                <div className="absolute -top-3 left-6 bg-gradient-to-r from-blue-500 to-blue-700 text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded">{t.ribbon}</div>
              )}
              <div className="text-sm font-semibold text-blue-300 mb-1" style={{ fontFamily: "Outfit" }}>{t.name}</div>
              <div className="text-xs text-slate-400 mb-4 min-h-[2em]">{t.tagline}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold text-white" style={{ fontFamily: "Outfit" }}>{annual ? t.priceAnnual : t.priceMonthly}</span>
                {t.suffix && <span className="text-xs text-slate-400">{t.suffix}</span>}
              </div>
              <div className="text-[11px] text-slate-500 mb-5 min-h-[1.5em]">{(annual ? t.noteAnnual : t.noteMonthly) || t.note || (t.id === "personal-pro" ? (annual ? "Billed annually" : "Billed monthly") : "")}</div>
              <Link to={t.id === "personal" ? "/register" : "/pricing"} data-testid={`splash-cta-${t.id}`}
                className={`block text-center w-full py-2.5 rounded-lg text-sm font-semibold ${t.highlight ? "bg-blue-600 text-white hover:bg-blue-500" : "border border-slate-700 text-slate-200 hover:border-blue-400"}`}>
                {t.cta}
              </Link>
            </div>
          ))}
        </div>

        <div className="pc-card overflow-x-auto" data-testid="splash-comparison">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/40">
                <th className="text-left p-4 text-xs uppercase tracking-widest text-slate-400 w-[32%]">Compare plans</th>
                {TIERS.map((t) => (
                  <th key={t.id} className="p-4 text-center text-xs uppercase tracking-widest text-blue-300/80">{t.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((grp) => (
                <React.Fragment key={grp.group}>
                  <tr className="bg-slate-900/20"><td colSpan={5} className="p-3 text-[10px] uppercase tracking-widest text-slate-500 border-t border-slate-800">{grp.group}</td></tr>
                  {grp.rows.map((r) => (
                    <tr key={`${grp.group}-${r.label}`} className="border-t border-slate-900/60 hover:bg-slate-900/30">
                      <td className="p-3 text-slate-300">{r.label}</td>
                      {r.vals.map((v, j) => <td key={`${r.label}-${j}`} className="p-3 text-center">{cellRender(v)}</td>)}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-center text-xs text-slate-500 mt-6">Powered by Stripe · 14-day free trial on Personal Pro · Annual plans invoiced yearly · Enterprise invoiced on net-30</p>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-[1300px] mx-auto px-6 py-20">
        <div className="pc-card p-10 relative overflow-hidden text-center">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-blue-500/20 blur-3xl rounded-full"></div>
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "Outfit" }}>Try it with your actual plan — not a demo.</h2>
            <p className="text-slate-300 max-w-xl mx-auto mb-6">Spin up your first project in under 15 minutes. If Compass PM saves you less than 6 hours of status prep in the first month, we don't want your money.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/register" data-testid="final-cta-btn" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-xl shadow-blue-900/40 hover:from-blue-400 hover:to-blue-600">
                Create free account <ArrowRight size={16}/>
              </Link>
              <a href="mailto:sales@upliftc.com?subject=Compass%20PM%20Enterprise" className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 text-slate-200 px-6 py-3 rounded-xl font-semibold hover:border-blue-400/40 hover:text-white">
                Talk to sales
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800/50 py-6 text-center text-xs text-slate-500">
        © 2026 Uplift Communications Corporation · Compass PM · <Link to="/privacy" className="text-slate-400 hover:text-white" data-testid="splash-privacy-link">Privacy</Link> · <Link to="/about" className="text-slate-400 hover:text-white" data-testid="splash-about-link">About</Link> · <Link to="/contact" className="text-slate-400 hover:text-white" data-testid="splash-contact-link">Contact</Link>
        <div className="mt-1">Questions? Email <a href="mailto:info@upliftc.com" className="text-blue-400 hover:text-blue-300" data-testid="splash-contact-email">info@upliftc.com</a> · <a href="https://www.linkedin.com/company/uci-compasspm/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300" data-testid="splash-linkedin-link"><Linkedin size={12} /> LinkedIn</a> · <a href="https://www.instagram.com/compasspm.app/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300" data-testid="splash-instagram-link"><Instagram size={12} /> Instagram</a> · <a href="https://www.trustpilot.com/review/compasspm.app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300" data-testid="splash-trustpilot-link"><Star size={12} /> Review us on Trustpilot</a></div>
      </footer>
    </div>
  );
}
